import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

/**
 * What a public page tells search engines, declared on its route as `data: { seo: { … } }`.
 *
 * Only Google and other crawlers that run JavaScript read these: the app is a single-page app,
 * so a link preview (Slack, iMessage, Facebook) only ever sees the static tags in index.html.
 */
export interface PageSeo {
    /** The search-result snippet. Without one the page keeps index.html's description. */
    description?: string;
    /** `false` for pages that must never appear in search: token links, one-time and per-organization pages. */
    index?: boolean;
    /** The canonical path when the URL can carry more than the page (the login matcher accepts `/auth/login/anything`). */
    canonicalPath?: string;
}

/**
 * Applies the deepest route's `PageSeo` on every navigation, and puts index.html's defaults back
 * on a route without one, so a description or `noindex` never outlives the page it was set for.
 * Called by `PayhoaTitleStrategy`, which already runs once per navigation with the final snapshot.
 */
@Injectable({ providedIn: 'root' })
export class PageSeoService {
    private readonly defaultDescription: string | null;

    constructor(
        private readonly meta: Meta,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {
        this.defaultDescription = this.meta.getTag('name="description"')?.content ?? null;
    }

    apply(snapshot: RouterStateSnapshot): void {
        const seo = PageSeoService.deepestSeo(snapshot.root);

        const description = seo?.description ?? this.defaultDescription;
        if (description) {
            this.meta.updateTag({ name: 'description', content: description });
        }

        if (seo?.index === false) {
            this.meta.updateTag({ name: 'robots', content: 'noindex' });
        } else {
            this.meta.removeTag('name="robots"');
        }

        // Canonical only on an indexable page that asked for SEO; a noindex page with a canonical
        // sends search engines two opposite signals.
        this.setCanonical(seo && seo.index !== false ? (seo.canonicalPath ?? PageSeoService.path(snapshot.url)) : null);
    }

    private setCanonical(path: string | null): void {
        let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (!path) {
            link?.remove();
            return;
        }
        if (!link) {
            link = this.document.createElement('link');
            link.rel = 'canonical';
            this.document.head.appendChild(link);
        }
        link.href = `${this.document.location.origin}${path}`;
    }

    private static deepestSeo(route: ActivatedRouteSnapshot | null): PageSeo | undefined {
        let seo: PageSeo | undefined;
        for (let at = route; at; at = at.firstChild) {
            seo = (at.data?.['seo'] as PageSeo | undefined) ?? seo;
        }
        return seo;
    }

    /** The URL's path alone: no query string, fragment or matrix params (`?uri=`, `;token=`). */
    private static path(url: string): string {
        const path = url
            .split(/[?#]/)[0]
            .split('/')
            .map(segment => segment.split(';')[0])
            .join('/');
        return path || '/';
    }
}
